import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { fetchCurrentAuthSession } from "../services/api";
import { API_URL, AUTH_SYNC_INTERVAL_MS } from "../config/env";
import type { AppRole, AuthContextValue, AuthSessionPayload, AuthUser } from "../types/auth";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const storedUser = localStorage.getItem("ae_user");
    return storedUser ? (JSON.parse(storedUser) as AuthUser) : null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("ae_token") || null);
  const userRef = useRef<AuthUser | null>(user);

  useEffect(() => {
    const handler = () => {
      userRef.current = null;
      setUser(null);
      setToken(null);
    };
    window.addEventListener("auth:logout", handler);
    return () => window.removeEventListener("auth:logout", handler);
  }, []);

  const resolvePrimaryRole = useCallback((roles: AppRole[] = []) => {
    if (!Array.isArray(roles) || roles.length === 0) return null;
    const rolePriority: AppRole[] = [
      "developer",
      "superadmin",
      "admin",
      "school_admin",
      "district_admin",
      "teacher",
      "special_services_teacher",
      "parent",
      "family",
      "student",
    ];
    return rolePriority.find((r) => roles.includes(r)) || roles[0];
  }, []);

  const login = useCallback(
    ({ user: nextUser, access_token, roles, preserveRole = false }: AuthSessionPayload) => {
      const roleList: AppRole[] = Array.isArray(roles)
        ? roles
        : Array.isArray(nextUser?.roles)
          ? nextUser.roles
          : [];
      const previous = userRef.current;
      const preservePreviousRole =
        preserveRole &&
        previous?.user_id != null &&
        previous.user_id === nextUser?.user_id &&
        roleList.includes(previous.role as AppRole);
      const normalizedUser: AuthUser = {
        ...nextUser,
        roles: roleList,
        role: preservePreviousRole
          ? previous!.role
          : roleList.includes(nextUser?.role as AppRole)
            ? nextUser.role
            : resolvePrimaryRole(roleList) || undefined,
      };

      userRef.current = normalizedUser;
      setUser((prev) =>
        JSON.stringify(prev) === JSON.stringify(normalizedUser) ? prev : normalizedUser,
      );
      setToken(access_token);
      localStorage.setItem("ae_user", JSON.stringify(normalizedUser));
      localStorage.setItem("ae_token", access_token);
    },
    [resolvePrimaryRole],
  );

  const switchRole = (role: AppRole) => {
    const currentUser = userRef.current;
    const switchableRoles: AppRole[] = ["teacher", "special_services_teacher", "parent", "family"];
    if (!currentUser || !switchableRoles.includes(role) || !currentUser.roles?.includes(role)) {
      return { ok: false, error: "This account does not have access to that view." };
    }
    const updatedUser = { ...currentUser, role };
    userRef.current = updatedUser;
    setUser(updatedUser);
    localStorage.setItem("ae_user", JSON.stringify(updatedUser));
    return { ok: true };
  };

  const logout = async () => {
    if (token) {
      await fetch(`${API_URL}/api/demo-users/logout-cleanup`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    userRef.current = null;
    setUser(null);
    setToken(null);
    localStorage.removeItem("ae_user");
    localStorage.removeItem("ae_token");
  };

  useEffect(() => {
    if (!token) return undefined;

    let cancelled = false;
    const syncSession = async () => {
      try {
        const data = await fetchCurrentAuthSession();
        if (cancelled) return;
        if (data?.access_token && data?.user) {
          const payload = data as AuthSessionPayload & {
            onboarding_required?: boolean;
            billing_status?: string | null;
            payment_required?: boolean;
          };
          const nextUser: AuthUser = { ...payload.user };
          const onboarding =
            payload.onboarding_required ?? payload.user?.onboarding_required;
          const paymentRequired =
            payload.payment_required ?? payload.user?.payment_required;
          const billingStatus =
            payload.billing_status ?? payload.user?.billing_status;
          // Only attach billing fields when the API actually sent them so
          // unchanged session syncs keep referential stability in tests/prod.
          if (onboarding !== undefined) {
            nextUser.onboarding_required = Boolean(onboarding);
          }
          if (paymentRequired !== undefined) {
            nextUser.payment_required = Boolean(paymentRequired);
          }
          if (billingStatus !== undefined) {
            nextUser.billing_status = billingStatus;
          }
          login({
            user: nextUser,
            access_token: data.access_token,
            roles: data.roles,
            preserveRole: true,
          });
        }
      } catch {
        if (cancelled) return;
      }
    };

    syncSession();
    const intervalId = window.setInterval(syncSession, AUTH_SYNC_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [token, login]);

  const switchSchool = async (schoolId: number | string) => {
    const numericSchoolId = Number(schoolId);
    if (!Number.isInteger(numericSchoolId) || numericSchoolId <= 0) {
      return { ok: false, error: "Invalid school id" };
    }

    try {
      const res = await fetch(`${API_URL}/api/auth/switch-school`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ school_id: numericSchoolId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { detail?: string }));
        return { ok: false, error: err.detail || "Switch failed" };
      }
      const data = await res.json();
      login({
        user: data.user,
        access_token: data.access_token,
        roles: data.roles,
        preserveRole: true,
      });
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.message || "Switch failed" };
    }
  };

  const value: AuthContextValue = { user, token, login, logout, switchSchool, switchRole };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
